import { Button, Icon, Step, Checkbox, Modal } from "semantic-ui-react";
import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { PaymentDialog } from "../../components/Payment/paymentDialog";
import useTemplate from "../../hooks/useTemplate";
import SignatureCanvas from "react-signature-canvas";
import { dateFormat, EntityType, TemplateType } from "../../components/shared/constants";
import { useMessageResources, useTextResources } from "../../language";
import moment from "moment";
import { GetReservationTotals, UpdateReservationStatus, UploadFile } from "../../api/services/pms";
import { ReservationStatusesEnums } from "../../components/shared/statustypes";
import { onError, onSuccess, onWarning } from "../../components/shared/messages";
import { UpdateGuest } from "../../api/services/crm";
import CapturePicDialog from "./CapturePicDialog";
import { ReceptionAnywhere } from "../../api/services/next";
import { getBase64 } from "../../components/shared/utils";
import { Images } from "../../assets/assets";
import GuestCard from "./GuestCard";
import { useCurrentHotel } from "../../hooks/useCurrentHotel"
import { CurrencyIds } from "../../components/shared/currencies";

export default function Wizard({ reservationDetails }) {
  const currentHotel = useCurrentHotel();
  const text = useTextResources();
  const message = useMessageResources();
  const [activeStep, setActiveStep] = useState(0);
  const [reservation, setReservation] = useState({});
  const [activePrivacyStep, setActivePrivacyStep] = useState();
  const [documentFiles, setDocumentFiles] = useState([]);
  const [passportFiles, setPassportFiles] = useState([]);
  const [signatureBase64, setSignatureBase64] = useState("");
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const termsAndConditionsTemplate = useTemplate({
    templateTypeId: TemplateType.TermsAndConditions,
    languageId: reservation?.Guest?.LanguageId,
  });
  const privacyPolicyTemplate = useTemplate({
    templateTypeId: TemplateType.PrivacyPolicy,
    languageId: reservation?.Guest?.LanguageId,
  });
  const signRef = useRef();
  const paymentDialogRef = useRef();

  useEffect(() => {
    (async () => {
      try {
        if (reservationDetails) {
          setReservation({ ...reservationDetails });
        }
      } catch (error) {
        onWarning(error);
      }
    })();
  }, [reservationDetails]);

  const changeReservationDetails = (value, field) => {
    let newReservation = { ...reservation };
    newReservation[field] = value;
    setReservation(newReservation);
  };

  const changeGuestDetails = (value, field) => {
    let newReservation = { ...reservation };
    newReservation.Guest = newReservation.Guest || {};
    newReservation.Guest[field] = value;
    setReservation(newReservation);
  };

  const changeLocationDetails = (value, field) => {
    let newReservation = { ...reservation };
    newReservation.Guest.Location = newReservation.Guest.Location || {};
    newReservation.Guest.Location[field] = value;
    setReservation(newReservation);
  };

  const handleFileUpload = async (files) => {
    if (files.length > 0) {
      try {
        for (const file of files) {
          const formData = new FormData();
          formData.append("EntityTypeID", EntityType.Reservation);
          formData.append("EntityID", reservation.Id);
          formData.append("Name", window.btoa(unescape(encodeURIComponent(file.name))));
          formData.append("Description", window.btoa(unescape(encodeURIComponent(file.name))));
          formData.append("files", file);
          await UploadFile(formData);
        }
      } catch (error) {
        onWarning("FileUploadError");
      }
    }
  };

  function dataURLtoFile(dataurl, filename) {
    var arr = dataurl.split(","),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], filename, { type: mime });
  }

  const onRecievePassportPhoto = (pic) => {
    setPassportFiles([
      ...passportFiles,
      { file: dataURLtoFile(pic, `${new Date().getTime()}passport.jpeg`), dataURL: pic },
    ]);
    setCameraDialogOpen(false);
  };

  const onRecieveDocumentPhoto = (pic) => {
    setDocumentFiles([
      ...documentFiles,
      { file: dataURLtoFile(pic, `${new Date().getTime()}image.jpeg`), dataURL: pic },
    ]);
    setCameraDialogOpen(false);
  };

  const validatePassportNumber = (str) => {
    return typeof str === "string" && str.length >= 8;
  };

  const validateGuestDetails = () => {
    if (!(signatureBase64.length > 0 && reservation.AgreedToPrivacyPolicy && reservation.AgreedToTermsAndConditions)) {
      onWarning(message("AgreeToTermsAndPrivaryAndSign"));
      return false;
    }
    if (
      !(reservation.Guest.FirstName && reservation.Guest.LastName && reservation.Guest.Email && reservation.Guest.Phone)
    ) {
      onWarning(`${text("GuestDetails")} ${text("Required")}`);
      return false;
    }
    if (!validatePassportNumber(reservation?.Guest?.PassportNumber)) {
      onWarning(message("PassportNotInFormat"));
      return false;
    }
    return true;
  };

  const saveReservation = async () => {
    if (validateGuestDetails()) {
      try {
        await handleFileUpload(documentFiles.map((file) => file.file));
        await handleFileUpload(passportFiles.map((file) => file.file));
        await UpdateGuest(reservation.Guest);
      } catch (error) {
        onWarning(error);
      }
      
      try {
        const pdf = await ReceptionAnywhere.GetCombinedRegistrationPdf({
          LanguageId: reservation?.Guest?.LanguageId,
          ReservationId: reservation.Id,
          TotalReservationAfterTax: reservation.TotalAmountCreditAfterTax,
          TotalReservationBeforeTax: reservation.TotalReservationBeforeTax,
          CurrencyCode: reservation.CurrencyID,
          Guest: reservation.Guest,
          Signature: signatureBase64,
        });
        await handleFileUpload([
          dataURLtoFile("data:application/pdf;base64," + pdf, `reservationPDF${reservation.Id}.pdf`),
        ]);
        onSuccess();
      } catch (error) {
        onWarning(message(`GenerationOfRegistrationPDFFailed`));
      }
    }
  };

  const checkInReservation = async () => {
    try {
      await UpdateReservationStatus({
        ...reservation,
        StatusId: ReservationStatusesEnums.CheckedIn,
      });
      onSuccess();
    } catch (error) {
      if(error.error.ErrorCode === 10441)
        onWarning(`${message("RoomDirty")} ${message("CannotPerformCheckIn")}`);
      else
        onWarning(error);
    }
  };

  const dateFormatter = (value) => {
    return value && moment(value).format(dateFormat);
  };

  const makePayment = async () => {
    try {
      if (paymentDialogRef.current) {
        const paymentResponse = await paymentDialogRef.current.saveAction();
        const updatedTotals = (await GetReservationTotals({
          ReservationIDs: [reservationDetails.Id],
          HotelID: reservationDetails.HotelId,
          TargetCurrencyId: currentHotel?.MainCurrencyId || CurrencyIds["USD"]
        }))[0];
        const newReservation = {
          ...reservation,
          TotalAmountCreditAfterTax: updatedTotals.TotalAmountCreditAfterTax,
          TotalAmountDebitAfterTax: updatedTotals.TotalAmountDebitAfterTax
        };
        setReservation(newReservation)
        if (paymentResponse)
          onSuccess();
        else
          onWarning(message("PleaseFillCreditCardDetails"));
      }
    } catch (error) {
      onError(error);
    }
  };

  const handlePassportFilesChange = async (newFiles) => {
    let fileURLs = await Promise.all(
      [...newFiles].map((file) => {
        if (isFileImage(file)) return getBase64(file);
        else return undefined;
      })
    );
    setPassportFiles([
      ...passportFiles,
      ...newFiles.map((file, fileIndex) => ({ file, dataURL: fileURLs[fileIndex] })),
    ]);
  };

  const handleDocumentsFilesChange = async (newFiles) => {
    let fileURLs = await Promise.all(
      [...newFiles].map((file) => {
        if (isFileImage(file)) return getBase64(file);
        else return undefined;
      })
    );
    setDocumentFiles([
      ...documentFiles,
      ...newFiles.map((file, fileIndex) => ({ file, dataURL: fileURLs[fileIndex] })),
    ]);
  };

  function isFileImage(file) {
    return file && file["type"]?.split("/")[0] === "image";
  }

  const steps = [
    <GuestCard key="guest-step" reservation={reservation} changeGuestDetails={changeGuestDetails} changeLocationDetails={changeLocationDetails} />,
    <div key="step2" style={{ display: "flex", gap: 8, justifyContent: "center" }}>
      <div className="image-upload">
        {passportFiles.length === 0 && (
          <div style={{ height: 50, textAlign: "center", marginBottom: 10 }}>{text("UploadPassportOrIDFiles")}</div>
        )}
        <div className="image-grid">
          {[...passportFiles].map((file, fileIndex) => (
            <div
              key={`document-${fileIndex}`}
              className="grid-image"
              style={{ backgroundImage: `url(${file.dataURL || Images.basic_file_icon})` }}
            >
              <Button
                icon="trash"
                circular
                size="mini"
                color="red"
                inverted
                onClick={() => setPassportFiles(passportFiles.filter((f, index) => index !== fileIndex))}
              ></Button>
            </div>
          ))}
          <div className="image-grid-upload-section">
            <Icon
              name="add"
              variant="outlined"
              onClick={() => document.querySelector("#hiddenPassportFileUploadInput").click()}
            ></Icon>
            <Icon size="large" name="camera" onClick={() => setCameraDialogOpen("passport")} />
          </div>
        </div>

        <input
          id="hiddenPassportFileUploadInput"
          style={{ display: "none" }}
          type="file"
          multiple
          accept=".msg,.doc,.word,.docx,.xlsx,.jpg,.jpeg,.gif,.png,.pdf,.tif"
          onChange={({ target }) => handlePassportFilesChange([...target.files])}
        />
      </div>
      <div className="image-upload">
        {documentFiles.length === 0 && (
          <div style={{ height: 50, textAlign: "center", marginBottom: 10 }}>{message("ReservationFilesUploaderSubtitle")}</div>
        )}
        <div className="image-grid">
          {[...documentFiles].map((file, fileIndex) => (
            <div
              key={`document-${fileIndex}`}
              className="grid-image"
              style={{ backgroundImage: `url(${file.dataURL})` }}
            >
              <span style={{ boxShadow: "0 0 5px white" }}>
              <Button
                icon="trash"
                circular
                size="mini"
                color="red"
                inverted
                onClick={() => setDocumentFiles(documentFiles.filter((f, index) => index !== fileIndex))}
              ></Button>
              </span>
            </div>
          ))}
          <div className="image-grid-upload-section">
            <Icon
              name="add"
              variant="outlined"
              onClick={() => document.querySelector("#hiddenDocumentsUploadInput").click()}
            ></Icon>
            <Icon size="large" name="camera" onClick={() => setCameraDialogOpen("documents")} />
          </div>
        </div>
      </div>
      <input
        id="hiddenDocumentsUploadInput"
        style={{ display: "none" }}
        type="file"
        multiple
        accept=".msg,.doc,.word,.docx,.xlsx,.jpg,.jpeg,.gif,.png,.pdf,.tif"
        onChange={({ target }) => handleDocumentsFilesChange([...target.files])}
      />
      <CapturePicDialog
        open={Boolean(cameraDialogOpen)}
        onClose={() => setCameraDialogOpen(false)}
        onSave={cameraDialogOpen === "passport" ? onRecievePassportPhoto : onRecieveDocumentPhoto}
      />
    </div>,
    <div key="step3" style={{ overflow: "auto" }}>
      <div className="payment">
        <PaymentDialog
          startOpen
          allowedEntities={[EntityType.Guest]}
          ref={paymentDialogRef}
          reservation={reservation}
          HotelId={reservation.HotelId}
          availableActions={[PaymentDialog.Actions.TakePayment]}
          defaultAction={PaymentDialog.Actions.TakePayment}
          currencyId={window.user?.CurrentHotel?.MainCurrencyId}
          isSinglePayment={true}
          amount={reservation?.TotalAmountDebitAfterTax + reservation?.TotalAmountCreditAfterTax}
        />
      </div>
    </div>,
    <div key="step4">
      <div style={{ display: "flex", alignItems: "center" }}>
        <Checkbox
          style={{ marginRight: 10 }}
          checked={Boolean(reservation?.AgreedToTermsAndConditions)}
          onClick={() =>
            changeReservationDetails(!reservation.AgreedToTermsAndConditions, "AgreedToTermsAndConditions")
          }
        />
        <span>{text("IAgreeTo")}</span>
        <span
          style={{ cursor: "pointer", color: "#004F75", marginLeft: 5 }}
          onClick={() => setActivePrivacyStep(termsAndConditionsTemplate)}
        >
          {termsAndConditionsTemplate?.subject}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Checkbox
          style={{ marginRight: 10 }}
          checked={Boolean(reservation?.AgreedToPrivacyPolicy)}
          onClick={() => changeReservationDetails(!reservation.AgreedToPrivacyPolicy, "AgreedToPrivacyPolicy")}
        />
        <span>{text("IAgreeTo")} </span>
        <span
          style={{ cursor: "pointer", color: "#004F75", marginLeft: 5 }}
          onClick={() => setActivePrivacyStep(privacyPolicyTemplate)}
        >
          {privacyPolicyTemplate?.subject}
        </span>
      </div>
      <Modal open={Boolean(activePrivacyStep)} size="large" scrolling>
        <Modal.Header>{activePrivacyStep?.subject}</Modal.Header>
        <Modal.Content className="modal-content-scrollable">
          <div>{activePrivacyStep?.body}</div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setActivePrivacyStep()}>{text("Close")}</Button>
        </Modal.Actions>
      </Modal>
      <div style={{ marginTop: 10 }}>
        <div>{text("Signature")}</div>
        <div style={{ position: "relative" }}>
          <span style={{ position: "absolute", top: 10, left: 10 }}>
            <Icon
              name="erase"
              onClick={() => {
                signRef.current.clear();
                setSignatureBase64("");
              }}
            />
          </span>
          <SignatureCanvas
            ref={signRef}
            onEnd={() => setSignatureBase64(signRef.current.toDataURL())}
            canvasProps={{ width: 500, height: 300 }}
            backgroundColor="#eeeeee"
          />
        </div>
      </div>
    </div>,
  ];
  return (
    <div className="wizard-contained">
      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5, magrin: "0 10px" }}
        className="reservation-details-grid"
      >
        <div className="reservation-summery-grid-title">{text("Number")}</div>
        <div className="reservation-summery-grid-title">{text("CheckIn")}</div>
        <div className="reservation-summery-grid-title">{text("CheckOut")}</div>
        <div className="reservation-summery-grid-title">{text("Nights")}</div>
        <div className="reservation-summery-grid-title">{text("Pax")}</div>
        <div className="reservation-summery-grid-title">{text("TotalPayment")}</div>
        <div className="reservation-summery-grid-title">{text("TotalBalance")}</div>
        <div>{reservation.Number}</div>
        <div>{dateFormatter(reservation.CheckInDate)}</div>
        <div>{dateFormatter(reservation.CheckOutDate)}</div>
        <div>{reservation.Nights}</div>
        <div>{`${reservation.Adults}, ${reservation.Children}, ${reservation.Infants}`}</div>
        <div>{reservation.TotalAmountCreditAfterTax?.toFixed(2)}</div>
        <div>{(reservation.TotalAmountDebitAfterTax + reservation.TotalAmountCreditAfterTax)?.toFixed(2)}</div>
      </div>
      <Step.Group size="mini">
        <Step active={activeStep === 0} onClick={() => setActiveStep(0)}>
          <Icon name="user" />
          <Step.Content>
            <Step.Title>{text("GuestDetails")}</Step.Title>
          </Step.Content>
        </Step>
        <Step active={activeStep === 1} onClick={() => setActiveStep(1)}>
          <Icon name="id card outline" />
          <Step.Content>
            <Step.Title>{`${text("Passport")} / ${text("ID")} / ${text("Documents")}`}</Step.Title>
          </Step.Content>
        </Step>
        <Step active={activeStep === 2} onClick={() => setActiveStep(2)}>
          <Icon name="credit card" />
          <Step.Content>
            <Step.Title>{text("Payment")}</Step.Title>
          </Step.Content>
        </Step>
        <Step active={activeStep === 3} onClick={() => setActiveStep(3)}>
          <Icon name="file alternate" />
          <Step.Content>
            <Step.Title>{text("Confirmation")}</Step.Title>
          </Step.Content>
        </Step>
      </Step.Group>
      <div>{steps[activeStep]}</div>
      <div className="save-check-in-tab">
        {activeStep === steps.length - 1 ? (
          <>
            <Button color="linkedin" onClick={saveReservation}>
              {text("Save")} 
            </Button>
            <Button color="linkedin" onClick={checkInReservation} style={{ width: 200 }}>
               {text("ReservationCheckIn")}
            </Button>
          </>
        ) : (
          <div>
            {activeStep === 2 && (
              <Button style={{ width: 160 }} onClick={makePayment}>
                {text("MakePayment")}
              </Button>
            )}
            <Button color="linkedin" onClick={() => setActiveStep(activeStep + 1)}>
              {text("Next")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

Wizard.propTypes = {
  onReturnToArrivals: PropTypes.func,
  reservationDetails: PropTypes.object,
};
